import { endOfYear, startOfYear } from "date-fns";
import type { Matcher } from "react-day-picker";

export const normalizeDate = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const getPresetKeyForRange = (
  fromValue?: Date,
  toValue?: Date
): string | null => {
  if (!fromValue || !toValue) return null;

  const from = normalizeDate(fromValue);
  const to = normalizeDate(toValue);
  if (to < from) return null;

  const today = normalizeDate(new Date());
  const isToToday = to.getTime() === today.getTime();

  if (isToToday) {
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();
    const oneMonthAgo = normalizeDate(
      new Date(currentYear, currentMonth - 1, currentDay)
    );
    const sixMonthsAgo = normalizeDate(
      new Date(currentYear, currentMonth - 6, currentDay)
    );
    const oneYearAgo = normalizeDate(
      new Date(currentYear - 1, currentMonth, currentDay)
    );

    if (from.getTime() === oneMonthAgo.getTime()) return "1M";
    if (from.getTime() === sixMonthsAgo.getTime()) return "6M";
    if (from.getTime() === oneYearAgo.getTime()) return "1Y";
  }

  const year = from.getFullYear();
  const startYear = startOfYear(new Date(year, 0, 1));
  const todayYear = today.getFullYear();
  const isCurrentYear = year === todayYear;
  const yearEnd = isCurrentYear ? today : normalizeDate(endOfYear(startYear));

  if (
    to.getFullYear() === year &&
    from.getTime() === startYear.getTime() &&
    to.getTime() === yearEnd.getTime()
  ) {
    return `year-${year}`;
  }

  return null;
};

export const parseInputDate = (rawValue: string): Date | undefined => {
  const trimmed = rawValue.trim();
  if (!trimmed) return undefined;

  const [dayStr, monthStr, yearStr] = trimmed.split(".");
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);

  if (
    !Number.isFinite(day) ||
    !Number.isFinite(month) ||
    !Number.isFinite(year)
  ) {
    return undefined;
  }

  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed;
};

export const mergeDisabledWithNoFuture = (
  disabledDays?: Matcher | Matcher[]
): Matcher[] => {
  const today = normalizeDate(new Date());
  const base: Matcher[] = [{ after: today }];

  if (!disabledDays) return base;
  if (Array.isArray(disabledDays)) {
    return [...base, ...disabledDays];
  }
  return [...base, disabledDays];
};
